import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { TextField, Grid, Select, MenuItem, FormControl, InputLabel, Button } from "@material-ui/core";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import { getLocalizedString } from "@icarius-localization/strings";
import { createDateFromDDMMYYYY } from "@icarius-utils/date";
import DateFnsUtils from "@date-io/date-fns";
import { dateLocale } from "@icarius-localization/strings";
import FabButton from "@icarius-common/fabButton";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { GalleryDialog } from "@icarius-common/galleryDialog";
import FileInput from "@icarius-common/fileUpload/fileInput";
import FileList from "@icarius-common/fileUpload/fileList";
import { getYesNoArray } from "@icarius-utils/constants";
import useHandleForm from "./useHandleForm";
import PollQuestion from "./pollQuestion";
import { createPollSettingsAction, modifyPollSettingsAction } from "../../actions";
import GalleryButtonAndDisplay from "@icarius-common/galleryDialog/components/galleryButtonAndDisplay";
import SelectionDialog from "@icarius-common/selectionDialog";
import Counter from "@icarius-common/counter";
import CustomNumberFormat from '@icarius-common/dynamicInputs/customNumberFormat';

const gridStyle = { height: 75, padding: "0px 10px" };

const Form = (props) => {

  const {
    data,
    pollNames,
    modes,
    processList,
    onaTypeList,
    positionList,
    visualizationTypeList,
    pollTypes,
    employeeList,
    closePoll,
  } = props;

  const [onaEmployeesDialogIsOpen, setOnaEmployeesDialogIsOpen] = useState(false);
  const [visualizationEmployeesDialogIsOpen, setVisualizationEmployeesDialogIsOpen] = useState(false);
  const [imagesDialogIsOpen, setImagesDialogIsOpen] = useState(false);

  const dispatch = useDispatch();

  const createPoll = (dataToSend) => {
    dispatch(createPollSettingsAction(dataToSend))
      .then((res) => res?.status === 200 && closePoll());
  }

  const modifyPoll = (dataToSend) => {
    dispatch(modifyPollSettingsAction(dataToSend))
      .then((res) => res?.status === 200 && closePoll());
  }

  const openValidationError = (type) => {
    if (type === "name") {
      dispatch(openSnackbarAction({ msg: getLocalizedString("surveyWithEnteredNameAlreadyExists"), severity: "error" }));
      return;
    }

    if (type === "ona") {
      dispatch(openSnackbarAction({
        msg: getLocalizedString("onaSurveyTypeError"),
        severity: "error",
        duration: null,
      }));
      return;
    }

    if (type === "nps") {
      dispatch(openSnackbarAction({
        msg: getLocalizedString("forNpsSurveyTypeOnly1QuestionWithOptionsCanBeCreatedContainingOptionsFrom1To10"),
        severity: "error",
        duration: null,
      }));
      return;
    }

    dispatch(openSnackbarAction({ msg: getLocalizedString("pollFieldsRequiredAndOptions"), severity: "error", duration: 10000 }));
  }

  const {
    isCreate,
    formData,
    setFormValue,
    createQuestion,
    deleteQuestion,
    duplicateQuestion,
    modifyQuestion,
    submit,
  } = useHandleForm(data, createPoll, modifyPoll, openValidationError, false, pollNames);

  return (
    <>
      <Grid container item xs={12} direction="row" style={{ paddingTop: 10 }}>
        <Grid container item alignItems="center" xs={12} sm={6} md={4} style={gridStyle}>
          <TextField
            disabled={data?.inUse || !isCreate}
            required
            fullWidth
            margin={"none"}
            label={getLocalizedString("pollName")}
            value={formData.name}
            onChange={(e) => setFormValue(e.target.value, "name")}
            inputProps={{ maxLength: 40 }}
          />
        </Grid>
        <Grid container item alignItems="center" xs={12} sm={6} md={2} style={gridStyle}>
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
            <KeyboardDatePicker
              disabled={data?.inUse}
              required
              fullWidth
              label={getLocalizedString("pollFrom")}
              cancelLabel={getLocalizedString("cancel")}
              okLabel={getLocalizedString("ok")}
              invalidDateMessage=""
              minDateMessage=""
              maxDateMessage=""
              format="dd/MM/yyyy"
              margin="none"
              value={formData.startDate}
              minDate={isCreate ? new Date() : null}
              onChange={(date) => setFormValue(date, "startDate")}
            />
          </MuiPickersUtilsProvider>
        </Grid>
        <Grid container item alignItems="center" xs={12} sm={6} md={2} style={gridStyle}>
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
            <KeyboardDatePicker
              required
              fullWidth
              label={getLocalizedString("pollTo")}
              cancelLabel={getLocalizedString("cancel")}
              okLabel={getLocalizedString("ok")}
              invalidDateMessage=""
              minDateMessage=""
              maxDateMessage=""
              minDate={data?.inUse ? createDateFromDDMMYYYY(data.endDate) : formData.startDate} //si esta en uso, que solo se pueda extender
              format="dd/MM/yyyy"
              margin="none"
              value={formData.endDate}
              onChange={(date) => setFormValue(date, "endDate")}
            />
          </MuiPickersUtilsProvider>
        </Grid>
        <Grid container item alignItems="center" xs={12} sm={6} md={4} style={gridStyle}>
          <FormControl style={{ width: "100%" }}>
            <InputLabel required id={`label-type-${data ? data.code : "new"}`}>{getLocalizedString("type")}</InputLabel>
            <Select
              disabled={data?.inUse}
              value={formData.type}
              labelId={`label-type-${data ? data.code : "new"}`}
              id={`select-type-${data ? data.code : "new"}`}
              onChange={(e) => setFormValue(e.target.value, "type")}
              margin={"none"}
            >
              {
                pollTypes?.map(item => (
                  <MenuItem
                    className={"whiteText"}
                    key={item.key}
                    value={item.key}>
                    {item.value}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </Grid>
        <Grid container item alignItems="center" xs={12} style={gridStyle}>
          <TextField
            disabled={data?.inUse}
            required
            fullWidth
            margin={"none"}
            label={getLocalizedString("pollDescription")}
            value={formData.description}
            onChange={(e) => setFormValue(e.target.value, "description")}
            inputProps={{ maxLength: 254 }}
          />
        </Grid>
        {
          formData.type !== "O" &&
          <>
            <Grid container item alignItems="center" xs={12} sm={6} md={2} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-useForOnboarding-${data ? data.code : "new"}`}>{getLocalizedString("onlyForOnboarding")}</InputLabel>
                <Select
                  disabled={data?.inUse}
                  value={formData.useForOnboarding}
                  labelId={`label-useForOnboarding-${data ? data.code : "new"}`}
                  id={`select-useForOnboarding-${data ? data.code : "new"}`}
                  onChange={(e) => setFormValue(e.target.value, "useForOnboarding")}
                  margin={"none"}
                >
                  {
                    getYesNoArray().map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} md={2} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-highlighted-${data ? data.code : "new"}`}>{getLocalizedString("viewAnswersAfterAnswering")}</InputLabel>
                <Select
                  disabled={data?.inUse}
                  value={formData.highlighted}
                  labelId={`label-highlighted-${data ? data.code : "new"}`}
                  id={`select-highlighted-${data ? data.code : "new"}`}
                  onChange={(e) => setFormValue(e.target.value, "highlighted")}
                  margin={"none"}
                >
                  {
                    getYesNoArray().map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} md={2} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-isAnonymous-${data ? data.code : "new"}`}>{getLocalizedString("anonymousFemale")}</InputLabel>
                <Select
                  disabled={data?.inUse}
                  value={formData.isAnonymous}
                  labelId={`label-isAnonymous-${data ? data.code : "new"}`}
                  id={`label-isAnonymous-${data ? data.code : "new"}`}
                  onChange={(e) => setFormValue(e.target.value, "isAnonymous")}
                  margin={"none"}
                >
                  {
                    getYesNoArray().map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-mode-${data ? data.code : "new"}`}>{getLocalizedString("modality")}</InputLabel>
                <Select
                  disabled={data?.inUse}
                  value={formData.mode}
                  labelId={`label-mode-${data ? data.code : "new"}`}
                  id={`select-isAnonymous-${data ? data.code : "new"}`}
                  onChange={(e) => setFormValue(e.target.value, "mode")}
                  margin={"none"}
                >
                  {
                    modes?.map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel shrink id={`label-process-${data ? data.code : "new"}`}>{getLocalizedString("relatedSpecialProcess")}</InputLabel>
                <Select
                  value={formData.process}
                  labelId={`label-process-${data ? data.code : "new"}`}
                  id={`select-process-${data ? data.code : "new"}`}
                  onChange={(e) => setFormValue(e.target.value, "process")}
                  margin={"none"}
                  displayEmpty
                >
                  <MenuItem className={"whiteText"} value={""}>
                    {getLocalizedString("none")}
                  </MenuItem>
                  {
                    processList?.map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
          </>
        }
        <Grid container item alignItems="center" xs={12} sm={formData.visualizationType === "EE" ? 6 : 12} style={gridStyle}>
          <FormControl style={{ width: "100%" }}>
            <InputLabel required id={`label-role-${data ? data.code : "new"}`}>{getLocalizedString("pollRole")}</InputLabel>
            <Select
              disabled={data?.inUse || formData.useForOnboarding === "Y"}
              value={formData.visualizationType}
              labelId={`label-role-${data ? data.code : "new"}`}
              id={`select-role-${data ? data.code : "new"}`}
              onChange={(e) => setFormValue(e.target.value, "visualizationType")}
              margin={"none"}
            >
              {
                visualizationTypeList?.map(item => (
                  <MenuItem
                    className={"whiteText"}
                    key={item.key}
                    value={item.key}
                  >
                    {item.value}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </Grid>
        {
          formData.visualizationType === "EE" &&
          <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
            <Button
              fullWidth
              className="whiteText"
              variant="contained"
              onClick={() => setVisualizationEmployeesDialogIsOpen(true)}
            >
              <span style={{ display: "flex", gap: 5 }}>
                {getLocalizedString("visualizationEmployees")}
                <Counter value={formData.visualizationEmployees?.length || 0} />
              </span>
            </Button>
          </Grid>
        }
        {
          formData.type === "O" &&
          <>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-onaType-${data ? data.code : "new"}`}>{getLocalizedString("onaOption")}</InputLabel>
                <Select
                  disabled={data?.inUse}
                  value={formData.onaType}
                  labelId={`label-onaType-${data ? data.code : "new"}`}
                  id={`select-onaType-${data ? data.code : "new"}`}
                  onChange={(e) => setFormValue(e.target.value, "onaType")}
                  margin={"none"}
                >
                  {
                    onaTypeList?.map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            {
              formData.onaType === "8" &&
              <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
                <FormControl style={{ width: "100%" }}>
                  <InputLabel required id={`label-onaPosition-${data ? data.code : "new"}`}>{getLocalizedString("position")}</InputLabel>
                  <Select
                    disabled={data?.inUse}
                    value={formData.onaPosition}
                    labelId={`label-onaPosition-${data ? data.code : "new"}`}
                    id={`select-onaPosition-${data ? data.code : "new"}`}
                    onChange={(e) => setFormValue(e.target.value, "onaPosition")}
                    margin={"none"}
                  >
                    {
                      positionList?.map(item => (
                        <MenuItem
                          className={"whiteText"}
                          key={item.key}
                          value={item.key}>
                          {item.value}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
            }
            {
              formData.onaType === "9" &&
              <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
                <Button
                  fullWidth
                  className="whiteText"
                  variant="contained"
                  onClick={() => setOnaEmployeesDialogIsOpen(true)}
                >
                  <span style={{ display: "flex", gap: 5 }}>
                    {getLocalizedString("onaEmployees")}
                    <Counter value={formData.onaEmployees?.length || 0} />
                  </span>
                </Button>
              </Grid>
            }
          </>
        }
        {
          ["C", "N"].includes(formData.type) &&
          <>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <TextField
                required
                fullWidth
                label={getLocalizedString("pollFamily")}
                margin={"none"}
                value={formData.family}
                onChange={(e) => setFormValue(e.target.value, "family")}
                inputProps={{ maxLength: 80 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <TextField
                required
                fullWidth
                label={getLocalizedString("pollReference")}
                margin={"none"}
                value={formData.reference}
                onChange={(e) => setFormValue(e.target.value, "reference")}
                inputProps={{ maxLength: 20 }}
              />
            </Grid>
          </>
        }
        {
          formData.isAnonymous === "N" &&
          <Grid container item alignItems="center" xs={12} style={gridStyle}>
            <TextField
              fullWidth
              margin={"none"}
              label={getLocalizedString("medalPoints")}
              InputProps={{ inputComponent: CustomNumberFormat }}
              value={formData.points}
              onChange={(e) => setFormValue(e.target.value, "points")}
              inputProps={{ min: "0", step: "1", decimals: 0 }}
            />
          </Grid>
        }
        <Grid container item alignItems="center" xs={12} style={{ ...gridStyle, height: "" }}>
          <FileInput
            acceptedFormats={[".jpg", ".bmp", ".png", ".jpeg", ".pdf"]}
            handleChange={(files) => setFormValue(files, "files")}
            id={`poll${data?.code || "create"}`}
          />
          <FileList
            files={formData.files}
            handleClick={() => setFormValue(null, "files")}
          />
        </Grid>
        <Grid container item xs={12} style={{ marginTop: 10, marginBottom: 10 }}>
          <GalleryButtonAndDisplay
            disabled={data?.inUse}
            image={formData.image}
            handleOpenGallery={() => setImagesDialogIsOpen(true)}
          />
        </Grid>
        <Grid container item xs={12}>
          {
            formData.questions?.map((question, index) =>
              <PollQuestion
                key={index}
                inUse={data?.inUse}
                isONAPoll={formData.type === "O"}
                index={index}
                data={question}
                hideDuplicate={formData.type === "N"}
                deleteQuestion={deleteQuestion}
                duplicateQuestion={duplicateQuestion}
                modifyQuestion={modifyQuestion}
              />
            )
          }
        </Grid>
        <Grid container item xs={12} justify={data?.inUse ? "flex-end" : "space-between"} alignItems="center" style={{ paddingBottom: 20 }}>
          {
            !data?.inUse &&
            <FabButton onClick={createQuestion} title={getLocalizedString("newPollQuestion")} />
          }
          <ButtonDialogAction isAccept onClick={submit} text={getLocalizedString("save")} />
        </Grid>
      </Grid>
      {
        imagesDialogIsOpen &&
        <GalleryDialog
          open={imagesDialogIsOpen}
          initialValue={formData.image}
          type={"news"}
          handleSubmit={(image) => setFormValue(image, "image")}
          handleClose={() => setImagesDialogIsOpen(false)}
        />
      }
      {
        onaEmployeesDialogIsOpen &&
        <SelectionDialog
          open={onaEmployeesDialogIsOpen}
          required={true}
          initialData={formData.onaEmployees}
          items={employeeList}
          disabledItems={data?.inUse ? employeeList.map((item) => item.key) : []} // si esta en uso, se ponen todos los items como disabled
          getItemAvatar={(item) => item.image}
          title={getLocalizedString("onaEmployees")}
          selectedTitle={getLocalizedString("selected")}
          handleSubmit={(employees) => setFormValue(employees, "onaEmployees")}
          handleClose={() => setOnaEmployeesDialogIsOpen(false)}
        />
      }
      {
        visualizationEmployeesDialogIsOpen &&
        <SelectionDialog
          open={visualizationEmployeesDialogIsOpen}
          required={true}
          initialData={formData.visualizationEmployees}
          items={employeeList}
          disabledItems={data?.inUse ? employeeList.map((item) => item.key) : []} // si esta en uso, se ponen todos los items como disabled
          getItemAvatar={(item) => item.image}
          title={getLocalizedString("visualizationEmployees")}
          selectedTitle={getLocalizedString("selected")}
          handleSubmit={(employees) => setFormValue(employees, "visualizationEmployees")}
          handleClose={() => setVisualizationEmployeesDialogIsOpen(false)}
        />
      }
    </>
  );
}

export default Form;